import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MDIconComponent, MDHeaderComponent, MDContentBoxComponent, MDToolTipDirective } from '@melodic-ui/components';
import { LessonDetailStateService } from '../lesson-detail-state.service';
import { LessonDetailFormStateService } from '../lesson-detail-form-state.service';
import { LessonSummaryWebhookService } from '../lesson-summary-webhook.service';
import { AudioTranscribeInputComponent } from '@lb/components/audio-transcribe';
import { RichTextViewComponent } from '@lb/components/rich-text-view';

@Component({
	selector: 'lb-lesson-detail-summary',
	templateUrl: './lesson-detail-summary.component.html',
	styleUrl: './lesson-detail-summary.component.scss',
	imports: [CommonModule, FormsModule, MDIconComponent, MDHeaderComponent, MDContentBoxComponent, MDToolTipDirective, AudioTranscribeInputComponent, RichTextViewComponent],
	standalone: true
})
export class LessonDetailSummaryComponent {
	readonly state = inject(LessonDetailStateService);
	private readonly formState = inject(LessonDetailFormStateService);
	private readonly webhookService = inject(LessonSummaryWebhookService);

	lesson = computed(() => this.state.lesson());
	isReadOnly = this.formState.isReadOnly;
	fieldsDisabled = this.formState.fieldsDisabled;

	readonly summary = this.formState.summary;
	readonly areasOfStrength = this.formState.areasOfStrength;
	readonly areasOfImprovement = this.formState.areasOfImprovement;
	/**
	 * Chirp rooms keep the notes field only — see LessonDetailFormStateService.collectsLessonFeedback.
	 * Shared with validation so a hidden field is never required.
	 */
	readonly collectsFeedback = this.formState.collectsLessonFeedback;
	readonly isGenerating = signal<boolean>(false);
	readonly generateError = signal<string | null>(null);
	readonly generatedSummary = signal<string | null>(null);

	/** Per-field required checks (true once submit is attempted and the field is empty). */
	isAreasOfStrengthInvalid = () => this.formState.isAreasOfStrengthInvalid();
	isAreasOfImprovementInvalid = () => this.formState.isAreasOfImprovementInvalid();

	/**
	 * Returns true if any required feedback field is invalid.
	 * Used to set content-box type to 'error' for red styling.
	 */
	isFeedbackInvalid = () => this.isAreasOfStrengthInvalid() || this.isAreasOfImprovementInvalid();

	onSummaryChange(value: string): void {
		this.formState.updateSummary(value);
	}

	onAreasOfStrengthChange(value: string): void {
		this.formState.updateAreasOfStrength(value);
	}

	onAreasOfImprovementChange(value: string): void {
		this.formState.updateAreasOfImprovement(value);
	}

	generate(): void {
		this.isGenerating.set(true);
		this.generateError.set(null);
		this.generatedSummary.set(null);

		this.webhookService.generateSummary().subscribe({
			next: (result) => {
				this.generatedSummary.set(result);
				this.isGenerating.set(false);
			},
			error: (err) => {
				console.error('Failed to generate lesson summary:', err);
				this.generateError.set('Failed to generate summary. Please try again.');
				this.isGenerating.set(false);
			}
		});
	}

	acceptGenerated(): void {
		const generated = this.generatedSummary();
		if (generated) {
			this.formState.updateSummary(generated);
			this.generatedSummary.set(null);
		}
	}

	discardGenerated(): void {
		this.generatedSummary.set(null);
	}
}
